// Practice Details → Integrations: "Contacts" (client texts, step 1b,
// docs/client-texts.md). Up to three staff contacts per practice, the first one
// primary; any staff member can open a contact's text thread (ChatPane), admins
// add, edit and remove contacts. The list shows the cell masked, as the server
// sends it; the full number appears only in the pane.
//
// Staff-visible (mounted beside the other !readOnly cards); the server enforces
// the role on every endpoint, `isAdmin` only hides the write buttons.

import React, { useEffect, useRef, useState } from "react";

import { looksLikePhone } from "../outreach/smsNumberApi";
import { SmsMessage } from "../outreach/SmsNumberParts";
import { MAX_CONTACTS, ROLES, createContact, fetchContacts, removeContact, roleLabel, updateContact } from "./api";
import ChatPane from "./ChatPane";
import "./ContactsCard.css";

const POSITIONS = [1, 2, 3];
const EMPTY_FORM = { name: "", cell: "", role: "office_manager", position: "" };

export default function ContactsCard({ practiceId, isAdmin, practiceName }) {
  const [contacts, setContacts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null); // {mode: 'add' | 'edit', id?, name, cell, role, position, active}
  const [confirmRemove, setConfirmRemove] = useState(null); // contact id
  const [chatContact, setChatContact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const busyRef = useRef(false);
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  useEffect(() => {
    setContacts(null);
    setLoadError(null);
    setForm(null);
    setConfirmRemove(null);
    setChatContact(null);
    setError(null);
    setMessage(null);
    if (!practiceId) return undefined;
    let cancelled = false;
    fetchContacts(practiceId)
      .then((list) => {
        if (!cancelled) setContacts(list);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  if (!practiceId) return null;

  const activeContacts = (contacts || []).filter((c) => c.active);
  const takenPositions = (contacts || []).map((c) => c.position);
  const freePosition = POSITIONS.find((p) => !takenPositions.includes(p)) || "";

  const startAdd = () => {
    setError(null);
    setMessage(null);
    setConfirmRemove(null);
    setForm({ mode: "add", ...EMPTY_FORM, position: freePosition });
  };

  const startEdit = (contact) => {
    setError(null);
    setMessage(null);
    setConfirmRemove(null);
    setForm({
      mode: "edit",
      id: contact.id,
      name: contact.name,
      cell: "",
      cellMasked: contact.cellMasked,
      role: contact.role,
      position: contact.position || "",
      active: contact.active,
    });
  };

  const setField = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  // Shared by Save and Remove: the result is dropped if the biller has switched
  // practice meanwhile, so A's contacts never land on B's card.
  const run = async (action, successText) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const forPractice = practiceId;
    try {
      const result = await action();
      if (currentPractice.current !== forPractice) return;
      const list = await fetchContacts(forPractice);
      if (currentPractice.current !== forPractice) return;
      setContacts(list);
      setForm(null);
      setConfirmRemove(null);
      setMessage(successText(result));
    } catch (err) {
      if (currentPractice.current !== forPractice) return;
      setError(err.message || "Could not save the contact.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const save = () => {
    const name = form.name.trim();
    const cell = form.cell.trim();
    if (!name) {
      setError("Enter the contact's name.");
      return;
    }
    if (form.mode === "add" && !cell) {
      setError("Enter the contact's cell number.");
      return;
    }
    if (cell && !looksLikePhone(cell)) {
      setError("Enter the cell as +1 then the digits (E.164), or a 10-digit US number.");
      return;
    }
    if (form.mode === "add") {
      run(
        () => createContact(practiceId, { name, cell, role: form.role, position: form.position || undefined }),
        (saved) => `Added ${saved.name}.`,
      );
    } else {
      run(
        () =>
          updateContact(practiceId, form.id, {
            name,
            cell: cell || undefined,
            role: form.role,
            position: form.position || undefined,
            active: form.active,
          }),
        (saved) => `Saved ${saved.name}.`,
      );
    }
  };

  const remove = (contact) =>
    run(
      () => removeContact(practiceId, contact.id),
      () => `Removed ${contact.name}. The thread is kept.`,
    );

  const canAdd = isAdmin && !form && activeContacts.length < MAX_CONTACTS;

  return (
    <div className="integrations-section ct-card">
      <div className="ct-card-head">
        <h3>Contacts</h3>
        {contacts && canAdd && (
          <button type="button" className="sms-btn" onClick={startAdd} disabled={busy}>
            Add contact
          </button>
        )}
      </div>

      {loadError ? (
        <SmsMessage tone="error">{loadError}</SmsMessage>
      ) : !contacts ? (
        <p className="sms-muted">Loading…</p>
      ) : (
        <>
          <p className="sms-intro">
            The practice's staff Atlas texts about its claims: up to three, the first one primary. Chat opens the thread;
            texts go out only when someone here clicks Send.
          </p>

          <SmsMessage tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </SmsMessage>
          <SmsMessage tone="error" onDismiss={() => setError(null)}>
            {error}
          </SmsMessage>

          {contacts.length === 0 && !form ? (
            <p className="sms-muted ct-empty">No contacts yet.</p>
          ) : (
            contacts.length > 0 && (
              <table className="ct-table">
                <thead>
                  <tr>
                    <th className="ct-col-pos">#</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Cell</th>
                    <th>Status</th>
                    <th className="ct-col-actions" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id} className={contact.active ? "" : "ct-row-inactive"}>
                      <td className="ct-col-pos">{contact.position ?? "—"}</td>
                      <td>
                        <span className="ct-name">{contact.name}</span>
                        {contact.position === 1 && <span className="ct-badge ct-badge-primary">primary</span>}
                      </td>
                      <td>{roleLabel(contact.role)}</td>
                      <td>
                        <span className="ct-cell">{contact.cellMasked || "•••-•••-••••"}</span>
                      </td>
                      <td>
                        <span className={`ct-badge ${contact.active ? "ct-badge-active" : "ct-badge-inactive"}`}>
                          {contact.active ? "active" : "inactive"}
                        </span>
                      </td>
                      <td className="ct-col-actions">
                        {confirmRemove === contact.id ? (
                          <span className="ct-confirm">
                            Remove {contact.name}?
                            <button
                              type="button"
                              className="sms-btn sms-btn-small sms-btn-danger"
                              onClick={() => remove(contact)}
                              disabled={busy}
                            >
                              {busy ? "Removing…" : "Yes, remove"}
                            </button>
                            <button
                              type="button"
                              className="sms-btn sms-btn-small"
                              onClick={() => setConfirmRemove(null)}
                              disabled={busy}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <span className="ct-actions">
                            <button
                              type="button"
                              className="sms-btn sms-btn-small sms-btn-primary"
                              onClick={() => setChatContact(contact)}
                              disabled={busy}
                            >
                              Chat
                            </button>
                            {isAdmin && (
                              <>
                                <button
                                  type="button"
                                  className="sms-btn sms-btn-small"
                                  onClick={() => startEdit(contact)}
                                  disabled={busy}
                                >
                                  Edit
                                </button>
                                {contact.active && (
                                  <button
                                    type="button"
                                    className="sms-btn sms-btn-small sms-btn-danger"
                                    onClick={() => {
                                      setForm(null);
                                      setError(null);
                                      setConfirmRemove(contact.id);
                                    }}
                                    disabled={busy}
                                  >
                                    Remove
                                  </button>
                                )}
                              </>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}

          {contacts.length === 0 && canAdd && (
            <div className="sms-actions">
              <button type="button" className="sms-btn sms-btn-primary" onClick={startAdd} disabled={busy}>
                Add contact
              </button>
            </div>
          )}

          {isAdmin && form && (
            <div className="sms-edit ct-form">
              <h4 className="ct-form-title">{form.mode === "add" ? "Add contact" : `Edit ${form.name || "contact"}`}</h4>
              <div className="sms-field">
                <label htmlFor={`ct-name-${practiceId}`}>Name</label>
                <input
                  id={`ct-name-${practiceId}`}
                  className="sms-input"
                  type="text"
                  value={form.name}
                  onChange={setField("name")}
                  placeholder="e.g. Office manager's name"
                  maxLength={120}
                  autoComplete="off"
                  disabled={busy}
                />
              </div>
              <div className="sms-field">
                <label htmlFor={`ct-cell-${practiceId}`}>Cell</label>
                <input
                  id={`ct-cell-${practiceId}`}
                  className="sms-input"
                  type="tel"
                  value={form.cell}
                  onChange={setField("cell")}
                  placeholder={form.mode === "edit" ? `leave blank to keep ${form.cellMasked}` : "+14155550123"}
                  autoComplete="off"
                  disabled={busy}
                />
              </div>
              <div className="ct-form-row">
                <div className="sms-field">
                  <label htmlFor={`ct-role-${practiceId}`}>Role</label>
                  <select
                    id={`ct-role-${practiceId}`}
                    className="sms-input"
                    value={form.role}
                    onChange={setField("role")}
                    disabled={busy}
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sms-field">
                  <label htmlFor={`ct-position-${practiceId}`}>Position</label>
                  <select
                    id={`ct-position-${practiceId}`}
                    className="sms-input"
                    value={form.position}
                    onChange={setField("position")}
                    disabled={busy}
                  >
                    <option value="">next free</option>
                    {POSITIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                        {p === 1 ? " (primary)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {form.mode === "edit" && (
                <label className="ct-check">
                  <input type="checkbox" checked={form.active} onChange={setField("active")} disabled={busy} />
                  Active (texts can be sent to this contact)
                </label>
              )}
              <div className="sms-actions">
                <button type="button" className="sms-btn" onClick={() => setForm(null)} disabled={busy}>
                  Cancel
                </button>
                <button type="button" className="sms-btn sms-btn-primary" onClick={save} disabled={busy}>
                  {busy ? "Saving…" : form.mode === "add" ? "Add contact" : "Save contact"}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {chatContact && (
        <ChatPane
          practiceId={practiceId}
          practiceName={practiceName}
          contactId={chatContact.id}
          contactName={chatContact.name}
          onClose={() => setChatContact(null)}
        />
      )}
    </div>
  );
}
